import shared from "../data/js/shared";
import { useUser, useUI, useChat, useProduct } from "../contexts";
import ChatInput from "./chat-input";
import UserBrief from "./user-brief";
import { Messages } from "./chat-messages";
import { useState, useEffect, useRef, RefObject, ComponentProps } from "react";
import Img from "./img";
import type { ChatCompanion, ChatMessage } from "../contexts/ChatMessageProvider";

const { buyerTitle, verifiedBuyerTitle, sellerTitle, verifiedSellerTitle } =
	shared.transaction;

const Title = ({ isSeller, companion }: { isSeller: boolean; companion: ChatCompanion }) => {
	return (
		<span className="title">
			{!isSeller
				? companion?.trustLevel === 2
					? verifiedSellerTitle
					: sellerTitle
				: companion?.trustLevel === 2
				? verifiedBuyerTitle
				: buyerTitle}
		</span>
	);
};


interface ChatWindowProps {
	id: string | number;
	isOwnListing: boolean;
	activeMessage: ChatMessage | null;
	setActiveMessage: (msg: ChatMessage | null) => void;
	onMessageTriggerRef?: (ref: RefObject<HTMLDivElement | null>) => void;
	onUserMenuTriggerRef?: (ref: RefObject<HTMLDivElement | null>) => void;
}

const ChatWindow = ({ id, isOwnListing, activeMessage, setActiveMessage, onMessageTriggerRef, onUserMenuTriggerRef }: ChatWindowProps) => {
	// Use individual context hooks
	const { profile } = useUser();
	const { setMessageMenuOpen, setChatUserMenuOpen } = useUI();
	const { chatMessages, chatCompanion, chatProduct, sendChatMessage, chatId, getMessages } = useChat();
	const { productDetail } = useProduct();

	const [chatMessage, setChatMessage] = useState("");
	const userMenuTriggerRef = useRef<HTMLDivElement>(null);

	// Pass the user menu trigger ref to parent
	useEffect(() => {
		if (onUserMenuTriggerRef) {
			onUserMenuTriggerRef(userMenuTriggerRef);
		}
	}, [onUserMenuTriggerRef]);
	const messages = chatMessages || [];
	const meId = profile?.id;
	const item = chatProduct || productDetail;
	const companion = chatCompanion || productDetail?.user;
	const isSeller = item?.user?.id == meId;
	
	// Load messages if we have a chat ID but no messages yet
	useEffect(() => {
		if (chatId && messages.length === 0) {
			void getMessages(chatId);
		}
	}, [chatId, messages.length, getMessages]);

	return (
		<div className="chat-window">
			{companion ? (
				<div className="chat-header">
					<UserBrief
						companion={companion as unknown as ComponentProps<typeof UserBrief>["companion"]}
						clickable={(companion as ChatCompanion).trustLevel >= 2}
						title={
							<Title isSeller={isSeller} companion={companion as ChatCompanion} />
						}
					/>
					<div
						ref={userMenuTriggerRef}
						className="chat-user-menu-trigger"
						onClick={() => setChatUserMenuOpen(true)}
						data-testid="chat-user-menu-trigger"
					>
						<Img className="dark-icon" src="/images/svg/vert-ellipsis.svg" alt="Menu" />
						<Img className="light-icon" src="/images/svg/vert-ellipsis-dark.svg" alt="Menu" />
					</div>
				</div>
			) : (
				""
			)}
			<div className="chat-messages-cont messages">
				<Messages
					messages={messages}
					meId={meId}
					newMessageCount={0}
					activeMessage={activeMessage}
					setActiveMessage={setActiveMessage}
					setMessageMenuOpen={setMessageMenuOpen}
					onTriggerRef={onMessageTriggerRef}
				/>
			</div>
			<ChatInput
				chatMessage={chatMessage}
				sendChatMessage={sendChatMessage}
				setChatMessage={setChatMessage}
				seller={isOwnListing}
				listingId={id}
				chatId={chatId}
			/>
		</div>
	);
};

export default ChatWindow;
